import { useState } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Salad, ChevronDown } from 'lucide-react';

// Catálogo de enfermedades diagnosticadas (según ficha de la nutrióloga)
const ENFERMEDADES = [
  'Diabetes tipo 1', 'Diabetes tipo 2', 'Hipertensión', 'Anemia', 'Reflujo',
  'Enfermedad renal', 'Colitis', 'Síndrome de intestino irritable', 'Gastritis',
  'Hipotiroidismo', 'Hipertiroidismo', 'Triglicéridos altos', 'Colesterol alto',
  'Obesidad', 'Síndrome de ovario poliquístico (SOP)',
];

const ALERGIAS = ['Gluten', 'Lactosa', 'Mariscos', 'Frutos secos', 'Huevo', 'Soya'];

const COMPLICACIONES = [
  { value: 'ninguna',              label: 'Ninguna' },
  { value: 'preeclampsia',         label: 'Preeclampsia' },
  { value: 'eclampsia',            label: 'Eclampsia' },
  { value: 'diabetes_gestacional', label: 'Diabetes gestacional' },
];

// Botón tipo "chip" para selección múltiple en una lista
const Chip = ({ active, onClick, children, color = 'emerald' }) => {
  const palette = color === 'amber'
    ? 'bg-amber-500 text-white border-amber-500'
    : 'bg-emerald-600 text-white border-emerald-600';
  const hover = color === 'amber' ? 'hover:border-amber-400' : 'hover:border-emerald-400';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2 py-1 rounded-full text-xs border transition-colors ${
        active ? palette : `border-border text-muted-foreground ${hover}`
      }`}
    >
      {children}
    </button>
  );
};

/**
 * Sección colapsable con toda la ficha nutriológica del paciente.
 * Trabaja sobre el estado del formulario del padre (formData / setFormData).
 */
export const NutriFichaFields = ({ formData, setFormData, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);

  const set = (campo, valor) => setFormData(prev => ({ ...prev, [campo]: valor }));

  const toggleEnLista = (campo, item) => {
    const lista = formData[campo] || [];
    set(campo, lista.includes(item) ? lista.filter(x => x !== item) : [...lista, item]);
  };

  const esFemenino = formData.sexo === 'femenino';

  return (
    <div className="rounded-lg border border-emerald-200 dark:border-emerald-900 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-emerald-50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-400 font-medium text-sm"
      >
        <span className="flex items-center gap-2">
          <Salad className="h-4 w-4" />
          Datos Nutriológicos
          <span className="text-xs font-normal text-emerald-600/70">(opcional)</span>
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="p-4 space-y-5">
          {/* Antropometría base */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label htmlFor="peso">Peso (kg)</Label>
              <Input id="peso" type="number" step="0.1" min="1" placeholder="70.5"
                value={formData.peso} onChange={e => set('peso', e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="estatura">Estatura (cm)</Label>
              <Input id="estatura" type="number" step="0.1" min="50" placeholder="165"
                value={formData.estatura} onChange={e => set('estatura', e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>IMC</Label>
              <div className="flex items-center h-10 px-3 rounded-md border bg-muted text-sm text-muted-foreground">
                {formData.peso && formData.estatura
                  ? (parseFloat(formData.peso) / Math.pow(parseFloat(formData.estatura) / 100, 2)).toFixed(1)
                  : '—'}
              </div>
            </div>
          </div>

          {/* Estilo de vida */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Ocupación / a qué se dedica</Label>
              <Input placeholder="Ej: oficinista" value={formData.ocupacion}
                onChange={e => set('ocupacion', e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Horas que pasa fuera de casa</Label>
              <Input placeholder="Ej: 8 horas" value={formData.horas_fuera_casa}
                onChange={e => set('horas_fuera_casa', e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>¿Quién prepara sus alimentos?</Label>
              <Input placeholder="Ej: ella misma" value={formData.quien_prepara_alimentos}
                onChange={e => set('quien_prepara_alimentos', e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>¿Dónde compra sus alimentos?</Label>
              <Input placeholder="Ej: mercado / supermercado" value={formData.donde_compra_alimentos}
                onChange={e => set('donde_compra_alimentos', e.target.value)} />
            </div>
          </div>

          {/* Objetivo + comidas */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Objetivo</Label>
              <Select value={formData.objetivo} onValueChange={v => set('objetivo', v)}>
                <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bajar_peso">Bajar de peso</SelectItem>
                  <SelectItem value="subir_peso">Subir de peso</SelectItem>
                  <SelectItem value="control_enfermedad">Control de enfermedad</SelectItem>
                  <SelectItem value="mantenimiento">Mantenimiento</SelectItem>
                  <SelectItem value="otro">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Comidas al día</Label>
              <Input type="number" min="1" max="10" placeholder="Ej: 3"
                value={formData.num_comidas_dia} onChange={e => set('num_comidas_dia', e.target.value)} />
            </div>
          </div>

          {/* Enfermedades diagnosticadas */}
          <div className="space-y-1">
            <Label>Enfermedades diagnosticadas</Label>
            <div className="flex flex-wrap gap-2">
              {ENFERMEDADES.map(ec => (
                <Chip key={ec} active={(formData.enfermedades_cronicas || []).includes(ec)}
                  onClick={() => toggleEnLista('enfermedades_cronicas', ec)}>
                  {ec}
                </Chip>
              ))}
            </div>
          </div>

          {/* Alergias / intolerancias */}
          <div className="space-y-1">
            <Label>Alergias / intolerancias alimentarias</Label>
            <div className="flex flex-wrap gap-2">
              {ALERGIAS.map(al => (
                <Chip key={al} color="amber" active={(formData.alergias_alimentarias || []).includes(al)}
                  onClick={() => toggleEnLista('alergias_alimentarias', al)}>
                  {al}
                </Chip>
              ))}
            </div>
          </div>

          {/* Alimentos que no le gustan */}
          <div className="space-y-1">
            <Label>Alimentos que no le gustan o no acostumbra consumir</Label>
            <Textarea rows={2} placeholder="Ej: hígado, betabel, pescado..."
              value={formData.alimentos_no_gusta} onChange={e => set('alimentos_no_gusta', e.target.value)} />
          </div>

          {/* Hábitos */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Actividad física</Label>
              <Select value={formData.actividad_fisica} onValueChange={v => set('actividad_fisica', v)}>
                <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sedentario">Sedentario</SelectItem>
                  <SelectItem value="ligero">Ligero</SelectItem>
                  <SelectItem value="moderado">Moderado</SelectItem>
                  <SelectItem value="intenso">Intenso</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Alcohol</Label>
              <Select value={formData.consume_alcohol} onValueChange={v => set('consume_alcohol', v)}>
                <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="no">No consume</SelectItem>
                  <SelectItem value="ocasional">Ocasional</SelectItem>
                  <SelectItem value="sí">Sí consume</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Tabaco</Label>
              <Select value={formData.fuma} onValueChange={v => set('fuma', v)}>
                <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="false">No fuma</SelectItem>
                  <SelectItem value="true">Sí fuma</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Grupo sanguíneo</Label>
              <Select value={formData.grupo_sanguineo} onValueChange={v => set('grupo_sanguineo', v)}>
                <SelectTrigger><SelectValue placeholder="Desconocido" /></SelectTrigger>
                <SelectContent>
                  {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(g => (
                    <SelectItem key={g} value={g}>{g}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Embarazo / lactancia (solo si sexo femenino) */}
          {esFemenino && (
            <div className="space-y-3 rounded-lg border border-pink-200 dark:border-pink-900 bg-pink-50/50 dark:bg-pink-950/10 p-3">
              <p className="text-sm font-medium text-pink-700 dark:text-pink-400">Embarazo / lactancia</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">¿Está embarazada?</Label>
                  <Select value={formData.embarazo} onValueChange={v => set('embarazo', v)}>
                    <SelectTrigger><SelectValue placeholder="No" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="false">No</SelectItem>
                      <SelectItem value="true">Sí</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formData.embarazo === 'true' && (
                  <div className="space-y-1">
                    <Label className="text-xs">Meses de embarazo</Label>
                    <Input type="number" min="1" max="10" value={formData.meses_embarazo}
                      onChange={e => set('meses_embarazo', e.target.value)} />
                  </div>
                )}
              </div>
              {formData.embarazo === 'true' && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Peso previo al embarazo (kg)</Label>
                    <Input type="number" step="0.1" value={formData.peso_pre_embarazo}
                      onChange={e => set('peso_pre_embarazo', e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Complicaciones</Label>
                    <Select value={formData.complicaciones_embarazo} onValueChange={v => set('complicaciones_embarazo', v)}>
                      <SelectTrigger><SelectValue placeholder="Ninguna" /></SelectTrigger>
                      <SelectContent>
                        {COMPLICACIONES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">¿Lactancia materna?</Label>
                  <Select value={formData.lactancia} onValueChange={v => set('lactancia', v)}>
                    <SelectTrigger><SelectValue placeholder="No" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="false">No</SelectItem>
                      <SelectItem value="true">Sí</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formData.lactancia === 'true' && (
                  <div className="space-y-1">
                    <Label className="text-xs">Meses de lactancia</Label>
                    <Input type="number" min="1" value={formData.meses_lactancia}
                      onChange={e => set('meses_lactancia', e.target.value)} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Diagnóstico nutricional */}
          <div className="space-y-1">
            <Label>Diagnóstico nutricional</Label>
            <Textarea rows={3} placeholder="Diagnóstico y observaciones de la nutrióloga..."
              value={formData.diagnostico_nutricional} onChange={e => set('diagnostico_nutricional', e.target.value)} />
          </div>
        </div>
      )}
    </div>
  );
};
